import detectEthereumProvider from "@metamask/detect-provider";
import { ethers } from "ethers";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import {
  ADMIN_WALLET_ADDRESS,
  CONTRACT_TOKEN,
  ETH_CONTRACT_TOKEN,
  NETWORKS,
} from "src/config";
import useAuth from "src/hooks/useAuth";
import abi from "src/pages/admin/financial/payout/components/request/abi";
import axiosInstance from "src/utils/axios";

const LS_KEY = "pending_metamask_recharges";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Entry shape:
// { txHash, amount, chainId, userId, timestamp,
//   status: 'broadcasting' | 'confirming' | 'confirmed_credit_pending' |
//           'reverted' | 'credit_failed' | 'expired',
//   lastCheckedAt, lastError }

export const loadPending = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

export const loadPendingForUser = (userId) => {
  return loadPending().filter(
    (e) => e.userId === undefined || e.userId === null || e.userId === userId
  );
};

const savePending = (list) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("pendingRechargesChanged"));
    }
  } catch {
    // Storage full or disabled. Recovery is best-effort.
  }
};

const addPending = (entry) => {
  const list = loadPending();
  if (!list.some((e) => e.txHash === entry.txHash)) {
    list.push(entry);
    savePending(list);
  }
};

const updatePending = (txHash, patch) => {
  const list = loadPending().map((e) =>
    e.txHash === txHash ? { ...e, ...patch } : e
  );
  savePending(list);
};

const removePending = (txHash) => {
  savePending(loadPending().filter((e) => e.txHash !== txHash));
};

const submitRechargeToServer = async (txHash, amount) => {
  const reqData = new FormData();
  reqData.append("transaction_id", String(txHash).trim());
  reqData.append("amount", amount);
  const { data } = await axiosInstance.post(
    "/api/user/recharge-wallet",
    reqData
  );
  return data;
};

let recoveryPromise = null;
export const runRecoveryOnce = (enqueueSnackbar, userId) => {
  if (!recoveryPromise) {
    recoveryPromise = recoverPendingRecharges(enqueueSnackbar, userId).finally(() => {
      setTimeout(() => {
        recoveryPromise = null;
      }, 30000);
    });
  }
  return recoveryPromise;
};

const useRechargeWithMetamask = (setLoading, reset, handleClose) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const userId = user?.id;

  const payIn = async (transaction_id, amount) => {
    try {
      const data = await submitRechargeToServer(transaction_id, amount);

      if (data.status) {
        removePending(transaction_id);
        enqueueSnackbar(data.message);
        reset();
      } else {
        updatePending(transaction_id, {
          status: "credit_failed",
          lastCheckedAt: Date.now(),
          lastError: data.message || "Recharge failed.",
        });
        enqueueSnackbar(
          `${data.message || "Recharge failed."} Your transaction hash: ${transaction_id}. We will retry automatically — see the Pending Recharges section on this page for status.`,
          { variant: "error", persist: true }
        );
      }
    } catch (err) {
      const serverMessage = err?.response?.data?.message || err.message;
      updatePending(transaction_id, {
        status: "credit_failed",
        lastCheckedAt: Date.now(),
        lastError: serverMessage,
      });
      enqueueSnackbar(
        `${serverMessage} Your transaction hash: ${transaction_id}. We will retry automatically — see the Pending Recharges section on this page for status.`,
        { variant: "error", persist: true }
      );
    } finally {
      setLoading(false);
    }
  };

  const amountToMetamask = async (chainId, amount) => {
    const {
      ETHEREUM: { CHAIN_ID: ethereumChainId },
    } = NETWORKS;

    const address =
      chainId === ethereumChainId ? ETH_CONTRACT_TOKEN : CONTRACT_TOKEN;
    const contract = await getStakingContract(address);

    // Save the return URL BEFORE prompting the wallet, so on mobile — where
    // signing deep-links out of the dapp and the WebView often reloads to "/"
    // on return — we can still bounce the user back to the recharge page even
    // if the dapp's JS context is killed mid-flow.
    try {
      localStorage.setItem(
        "metamaskReturnUrl",
        JSON.stringify({
          url: window.location.pathname + window.location.search,
          timestamp: Date.now(),
        })
      );
    } catch {
      // localStorage unavailable; skip.
    }

    // Show the spinner immediately. The original code only showed it after the
    // wallet returned the tx, which on mobile meant the user came back from
    // MetaMask to a UI that looked unchanged and clicked Submit again.
    setLoading(true);
    handleClose();

    try {
      // Read decimals from the token contract directly. The previous hardcoded
      // ternary assumed real USDT (6 on ETH, 18 on BSC) and silently sent the
      // wrong base-units amount for any other token. Falls back to the guess
      // only if the contract doesn't expose decimals().
      let decimals;
      try {
        const raw = await contract.decimals();
        decimals =
          typeof raw === "number"
            ? raw
            : typeof raw?.toNumber === "function"
            ? raw.toNumber()
            : Number(raw);
        if (!Number.isFinite(decimals) || decimals < 0 || decimals > 30) {
          throw new Error(`Unexpected decimals: ${raw}`);
        }
      } catch {
        decimals = chainId === ethereumChainId ? 6 : 18;
      }

      const tx = await contract.transfer(
        ADMIN_WALLET_ADDRESS,
        ethers.utils.parseUnits(amount.toString(), decimals),
        { gasLimit: 200000 }
      );

      // If the wallet returns a tx with no hash (rare; some mobile wallets
      // resolve the promise without populating .hash), fail loudly — never
      // submit an empty hash to the backend.
      if (!tx?.hash) {
        enqueueSnackbar(
          "Your wallet did not return a transaction hash. Please check your wallet history and contact support if value was deducted.",
          { variant: "error", persist: true }
        );
        setLoading(false);
        return;
      }

      // Persist before tx.wait() so a closed tab / lost network during
      // confirmation can be recovered on the next page load.
      addPending({
        txHash: tx.hash,
        amount,
        chainId,
        userId,
        timestamp: Date.now(),
        status: "confirming",
      });

      const receipt = await tx.wait();
      if (receipt.status === 1) {
        updatePending(tx.hash, {
          status: "confirmed_credit_pending",
          lastCheckedAt: Date.now(),
        });
        await payIn(tx.hash, amount);
      } else {
        updatePending(tx.hash, { status: "reverted", lastCheckedAt: Date.now() });
        removePending(tx.hash);
        enqueueSnackbar("Transaction reverted on the blockchain.", {
          variant: "error",
        });
        setLoading(false);
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "Transaction failed", {
        variant: "error",
      });
      handleClose();
      reset();
      setLoading(false);
    }
  };

  useEffect(() => {
    runRecoveryOnce(enqueueSnackbar, userId).catch(() => {});
  }, [enqueueSnackbar, userId]);

  return { amountToMetamask };
};

// Try to verify the tx on-chain via a public RPC for the entry's chain.
// Returns:
//   { confirmed: true }  → receipt status 1
//   { reverted: true }   → receipt status 0
//   { pending: true }    → no receipt yet
//   { unknown: true }    → couldn't reach the chain
const checkOnChain = async (entry) => {
  let rpcUrl = null;
  Object.values(NETWORKS).forEach((net) => {
    if (net.CHAIN_ID === entry.chainId && net.RPC_URL) rpcUrl = net.RPC_URL;
  });

  if (rpcUrl) {
    try {
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      const receipt = await provider.getTransactionReceipt(entry.txHash);
      if (!receipt) return { pending: true };
      return receipt.status === 1 ? { confirmed: true } : { reverted: true };
    } catch {
      // RPC unreachable — fall through to MetaMask fallback.
    }
  }

  try {
    const metamaskProvider = await detectEthereumProvider();
    if (metamaskProvider) {
      const ethersProvider = new ethers.providers.Web3Provider(metamaskProvider);
      const receipt = await ethersProvider.getTransactionReceipt(entry.txHash);
      if (!receipt) return { pending: true };
      return receipt.status === 1 ? { confirmed: true } : { reverted: true };
    }
  } catch {
    // Provider not ready — caller can decide what to do.
  }

  return { unknown: true };
};

const recoverPendingRecharges = async (enqueueSnackbar, currentUserId) => {
  const all = loadPending();
  if (all.length === 0) return;

  // Skip entries belonging to a different signed-in user on the same device.
  // Entries from before user-scoping was added (no userId field) are still processed.
  const mine = all.filter(
    (e) => e.userId === undefined || e.userId === null || e.userId === currentUserId
  );
  if (mine.length === 0) return;

  const now = Date.now();
  const expired = mine.filter((e) => now - (e.timestamp || 0) >= TTL_MS);
  const fresh = mine.filter((e) => now - (e.timestamp || 0) < TTL_MS);

  if (expired.length > 0) {
    savePending(fresh);
    enqueueSnackbar(
      `${expired.length} unconfirmed recharge(s) older than 7 days could not be auto-recovered. Please contact support with hash(es): ${expired
        .map((e) => e.txHash)
        .join(", ")}`,
      { variant: "warning", persist: true }
    );
  }
  if (fresh.length === 0) return;

  for (const entry of fresh) {
    const status = await checkOnChain(entry);

    if (status.reverted) {
      updatePending(entry.txHash, { status: "reverted", lastCheckedAt: Date.now() });
      removePending(entry.txHash);
      continue;
    }

    // Pending / unknown / confirmed: submit to backend. The backend's idempotency
    // check makes repeat submits safe, and we don't want to leave the user
    // stranded when MetaMask isn't available on mobile post-reload.
    if (status.pending) {
      updatePending(entry.txHash, { status: "confirming", lastCheckedAt: Date.now() });
    } else if (status.confirmed) {
      updatePending(entry.txHash, {
        status: "confirmed_credit_pending",
        lastCheckedAt: Date.now(),
      });
    }

    try {
      const data = await submitRechargeToServer(entry.txHash, entry.amount);
      if (data.status) {
        removePending(entry.txHash);
        enqueueSnackbar(
          `Recovered a previous recharge of ${entry.amount} — your balance has been credited.`,
          { variant: "success" }
        );
      } else {
        updatePending(entry.txHash, {
          status: "credit_failed",
          lastCheckedAt: Date.now(),
          lastError: data.message || "Backend declined the credit.",
        });
      }
    } catch (e) {
      updatePending(entry.txHash, {
        status: "credit_failed",
        lastCheckedAt: Date.now(),
        lastError: e?.response?.data?.message || e?.message || "Network error",
      });
    }
  }
};

export const triggerRecoveryForEntry = async (txHash) => {
  const entry = loadPending().find((e) => e.txHash === txHash);
  if (!entry) return { ok: false, message: "Entry not found." };

  const status = await checkOnChain(entry);

  if (status.reverted) {
    updatePending(entry.txHash, { status: "reverted", lastCheckedAt: Date.now() });
    removePending(entry.txHash);
    return { ok: false, message: "Transaction reverted on the blockchain." };
  }

  if (status.pending) {
    updatePending(entry.txHash, { status: "confirming", lastCheckedAt: Date.now() });
  } else if (status.confirmed) {
    updatePending(entry.txHash, {
      status: "confirmed_credit_pending",
      lastCheckedAt: Date.now(),
    });
  }

  try {
    const data = await submitRechargeToServer(entry.txHash, entry.amount);
    if (data.status) {
      removePending(entry.txHash);
      return { ok: true, message: data.message || "Credited." };
    }
    updatePending(entry.txHash, {
      status: "credit_failed",
      lastCheckedAt: Date.now(),
      lastError: data.message,
    });
    return { ok: false, message: data.message || "Backend declined the credit." };
  } catch (e) {
    const msg = e?.response?.data?.message || e?.message || "Unknown error";
    updatePending(entry.txHash, {
      lastCheckedAt: Date.now(),
      lastError: msg,
    });
    return { ok: false, message: msg };
  }
};

export default useRechargeWithMetamask;

const getStakingContract = async (token) => {
  const signer = await getSigner();
  const tokenContract = new ethers.Contract(token, abi, signer);
  return tokenContract;
};

const getSigner = async () => {
  const metamaskProvider = await detectEthereumProvider();
  const ethersProvider = new ethers.providers.Web3Provider(metamaskProvider);
  return ethersProvider.getSigner();
};
